import { isEqual, isUndefined, omitBy } from 'lodash'

import { IOrganization } from '@/models'

/** what the Settings and Pricing tier tabs save together with one Save changes */
export type OrganizationDraft = {
  organization: Partial<IOrganization>
  tags: string[]
  segments: string[]
  pricingTierId?: string
  overrides?: Record<string, unknown>
}

const ORGANIZATION_FIELDS: { key: keyof IOrganization; label: string }[] = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'customer_number', label: 'Customer number' },
  { key: 'severity_group', label: 'Severity group' },
  { key: 'organization_use', label: 'Environment' },
  { key: 'parent_production_org_id', label: 'Parent production organization' },
  { key: 'subscription_status', label: 'Subscription status' },
  { key: 'subscription_expiry_date', label: 'Subscription end date' },
  { key: 'company_size', label: 'Company size' },
  { key: 'hubspot_company_id', label: 'HubSpot company' }
]

const isBlank = (value: unknown) =>
  value === undefined || value === null || value === ''

const sameValue = (a: unknown, b: unknown) =>
  (isBlank(a) && isBlank(b)) || isEqual(a, b)

const sameItems = (a: string[] = [], b: string[] = []) =>
  isEqual([...a].sort(), [...b].sort())

/** overrides that match the tier don't change anything, the backend drops them too */
const normalizeOverrides = (
  overrides: Record<string, unknown> = {},
  tierSettings: Record<string, unknown> = {}
) =>
  omitBy(
    overrides,
    (value, key) => isUndefined(value) || isEqual(value, tierSettings[key])
  )

/** labels of everything that differs between the saved state and the draft, in display order */
export const getUnsavedChanges = (
  saved: OrganizationDraft,
  draft: OrganizationDraft,
  tierSettings?: Record<string, unknown>
): string[] => {
  const changes = ORGANIZATION_FIELDS.filter(
    ({ key }) => !sameValue(saved.organization[key], draft.organization[key])
  ).map(({ label }) => label)

  if (!sameItems(saved.segments, draft.segments)) changes.push('Segments')
  if (!sameItems(saved.tags, draft.tags)) changes.push('Tags')
  if (!sameValue(saved.pricingTierId, draft.pricingTierId)) {
    changes.push('Pricing tier')
  } else if (
    !isEqual(
      normalizeOverrides(saved.overrides, tierSettings),
      normalizeOverrides(draft.overrides, tierSettings)
    )
  ) {
    changes.push('Pricing tier overrides')
  }

  return changes
}
